// src/rangedFetch.ts
// Fetches a same-origin file in ranged chunks instead of one plain GET: the
// API won't buffer a whole large video or song in one response (see
// api/src/blobServe.ts's maxWholeBytes). Used for downloading renders
// (projectsApi.ts) and by the draft preview for music (preview/stage.ts).

/** Matches the server's range cap (blobServe.ts's MAX_RANGE_CHUNK). */
const CHUNK = 8 * 1024 * 1024;

export interface RangedFile {
  blob:        Blob;
  /** The first response's headers (Content-Disposition, Content-Type). */
  headers:     Headers;
}

/** Thrown for a response that isn't 200 or 206, with the server's JSON `error` if it sent one. */
export class RangedFetchError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export async function fetchInChunks(url: string, onProgress?: (fraction: number) => void): Promise<RangedFile> {
  const parts: Blob[] = [];
  let headers: Headers | null = null;
  let offset = 0;
  let total = Infinity;
  while (offset < total) {
    const res = await fetch(url, { credentials: 'include', headers: { Range: `bytes=${offset}-${offset + CHUNK - 1}` } });
    if (res.status !== 206 && res.status !== 200) {
      const data: Record<string, unknown> = await res.json().catch(() => ({}));
      throw new RangedFetchError(res.status, typeof data.error === 'string' ? data.error : `Download failed (${res.status})`);
    }
    headers ??= res.headers;
    const blob = await res.blob();
    parts.push(blob);
    offset += blob.size;
    // A 200 is the whole file (a server that ignored Range); a 206 says the total after the slash.
    total = res.status === 200 ? offset : Number(/\/(\d+)$/.exec(res.headers.get('Content-Range') ?? '')?.[1] ?? offset);
    if (blob.size === 0) break;
    onProgress?.(Math.min(1, offset / total));
  }
  const contentType = headers?.get('Content-Type') ?? 'application/octet-stream';
  return { blob: new Blob(parts, { type: contentType }), headers: headers ?? new Headers() };
}
